import { Fragment, useMemo, useState } from "react";
import { useCallHistory, useCallHistoryCount, type CallHistoryFilters } from "@/hooks/use-calls";
import { channelLabel, formatDuration } from "@/lib/calls";
import type { CallGroup } from "@/types";
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, FileText } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { Section } from "@/components/ui/section";
import { Toolbar } from "@/components/ui/toolbar";
import { Select } from "@/components/ui/select";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";

const PAGE_SIZE = 50;

function formatTime(ts: string | null): string {
  if (!ts) return "-";
  return new Date(ts).toLocaleString();
}

/** Rolling windows, in hours. "All time" is deliberately not one of these. */
const DATE_PRESETS = [
  { label: "24h", hours: 24 },
  { label: "7d", hours: 168 },
  { label: "30d", hours: 720 },
];

/** Sentinel for "no date filter at all" — not a period, so it renders apart. */
const ALL_TIME = "All";

const presetButtonClass = (active: boolean, inactiveBorder = "border-border") =>
  `cursor-pointer rounded-sm border px-2 py-1 text-xs transition-colors duration-150 ${
    active
      ? "border-border-active bg-accent font-medium text-foreground"
      : `${inactiveBorder} text-muted-foreground hover:bg-accent/60 hover:text-foreground`
  }`;

/** Tooltip spelling out both identities, since the column shows only one. */
function callIdTitle(call: CallGroup): string {
  const lines: string[] = [];
  const ids = call.sip_call_ids?.length
    ? call.sip_call_ids
    : call.sip_call_id
      ? [call.sip_call_id]
      : [];
  if (ids.length) {
    lines.push(
      ids.length > 1
        ? `SIP Call-IDs (one per dialog):\n${ids.map((id) => `  ${id}`).join("\n")}`
        : `SIP Call-ID: ${ids[0]}`
    );
  } else {
    lines.push("No SIP Call-ID — this call has no PJSIP leg.");
  }
  if (call.linked_id) lines.push(`Asterisk linkedid: ${call.linked_id}`);
  return lines.join("\n");
}

const DIRECTION_OPTIONS = [
  { label: "All directions", value: "" },
  { label: "Inbound", value: "inbound" },
  { label: "Outbound", value: "outbound" },
  { label: "Internal", value: "internal" },
];

export default function CallHistoryPage() {
  const [page, setPage] = useState(0);
  const [direction, setDirection] = useState("");
  const [datePreset, setDatePreset] = useState(ALL_TIME);
  //: ISO start of the selected window, pinned at the moment it was selected.
  const [dateFrom, setDateFrom] = useState<string | null>(null);
  // Now the linked_id of a call, not a call_log primary key — /api/calls
  // returns one row per call and `id` is that group key. Still a string, so
  // the comparison against `call.id` below is unchanged.
  const [expandedCallId, setExpandedCallId] = useState<string | null>(null);

  // The window is pinned when the preset is chosen, not re-derived as time
  // passes. It is part of the react-query queryKey, so anything that moves it
  // invalidates the cache: computing it during render moved it on every render
  // and the query refetched in a loop, and re-deriving it on a timer re-keyed
  // the query every minute, blanking the table to a skeleton each time. Neither
  // bought anything here — the user picked "last 24h", they do not need it to
  // slide underneath them. Refresh, or re-clicking a preset, re-pins it.
  const filters: CallHistoryFilters = useMemo(() => {
    const next: CallHistoryFilters = {};
    if (direction) next.direction = direction;
    if (dateFrom) next.date_from = dateFrom;
    return next;
  }, [direction, dateFrom]);

  const selectPreset = (label: string) => {
    const preset = DATE_PRESETS.find((p) => p.label === label);
    setDatePreset(label);
    setDateFrom(
      preset ? new Date(Date.now() - preset.hours * 3600 * 1000).toISOString() : null
    );
    setPage(0);
  };

  const skip = page * PAGE_SIZE;
  const { data: calls, isLoading, isFetching, refetch } = useCallHistory(skip, PAGE_SIZE, filters);
  const { data: countData } = useCallHistoryCount(filters);
  // COUNT(DISTINCT linked_id): calls, not call_log rows. It reads far lower
  // than it used to (22 where the page said 59) because one call is several
  // channels, so the channel total is shown next to it — nothing was lost.
  const total = countData?.count ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const callList = Array.isArray(calls) ? calls : [];
  // /calls/count reports the channel total for the same filters, so this holds
  // across every page rather than only the rows currently loaded.
  const totalChannels = countData?.channel_count ?? 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Call History"
        description="Completed calls with SIP details. History is retained for 7 days."
      />

      <Section title="Call records">
      <div className="space-y-4">
      <Toolbar
        filters={
          <>
            <Select
              value={direction}
              onChange={(e) => {
                setDirection(e.target.value);
                setPage(0);
              }}
              className="h-8 w-40 text-xs"
            >
              {DIRECTION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
            <div className="flex items-center gap-0.5">
              {DATE_PRESETS.map((p) => (
                <button
                  key={p.label}
                  onClick={() => selectPreset(p.label)}
                  className={presetButtonClass(datePreset === p.label)}
                  title={`Calls from the last ${p.label}`}
                >
                  {p.label}
                </button>
              ))}
              {/* "All time" is not another window on the same axis — it removes
                  the date filter entirely — so it sits past a divider and keeps
                  a dashed outline while inactive. */}
              <span className="mx-1.5 h-4 w-px bg-border" aria-hidden />
              <button
                onClick={() => selectPreset(ALL_TIME)}
                className={presetButtonClass(
                  datePreset === ALL_TIME,
                  "border-dashed border-border"
                )}
                title="No date filter — every call still in the database"
              >
                All time
              </button>
            </div>
          </>
        }
        count={
          <span className="inline-flex items-center gap-1">
            <span>{total} calls</span>
            {totalChannels > 0 && (
              <span
                className="text-muted-foreground/70"
                title="One call is several Asterisk channels — dialling out through a trunk is a Local channel pair plus the outbound leg. Calls are counted per linked_id; expand a row to see its channels."
              >
                · {totalChannels} channels
              </span>
            )}
          </span>
        }
        // Re-pins the window as well as refetching, so Refresh on "24h" means
        // the last 24 hours from now rather than from when the preset was
        // clicked. selectPreset is a no-op for All time, which has no window.
        onRefresh={() => {
          if (datePreset !== ALL_TIME) selectPreset(datePreset);
          refetch();
        }}
        refreshing={isFetching}
      />

      {isLoading ? (
        <div className="space-y-2 rounded-sm border border-border bg-background p-4">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      ) : callList.length === 0 ? (
        <EmptyState
          icon={<FileText size={24} />}
          title="No call history"
          description="Completed calls appear here. Adjust the direction or period filters to widen the search."
          className="rounded-sm border border-border bg-background"
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Time</TableHead>
              <TableHead>Call ID</TableHead>
              <TableHead>Caller</TableHead>
              <TableHead>Connected</TableHead>
              <TableHead>Direction</TableHead>
              <TableHead>State</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Hangup Cause</TableHead>
              <TableHead className="w-8" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {callList.map((call) => {
              const isExpanded = expandedCallId === call.id;
              const hasHeaders =
                call.invite_headers && Object.keys(call.invite_headers).length > 0;
              const legs = call.channels ?? [];
              // The chevron used to appear only when a call carried SIP invite
              // headers, which hid the per-leg breakdown behind nothing.
              const canExpand = legs.length > 0 || hasHeaders;
              return (
                <Fragment key={call.id}>
                <TableRow className={isExpanded ? "bg-muted/30" : undefined}>
                  <TableCell className="whitespace-nowrap text-xs">
                    {formatTime(call.start_time)}
                  </TableCell>
                  {/* The SIP Call-ID, not Asterisk's linkedid: this is the value
                      the trunk provider, a pcap and HOMER all key on. linkedid
                      is kept in the tooltip and the expanded rows because it is
                      what correlates with this PBX's own logs. */}
                  <TableCell
                    className="max-w-[190px] truncate font-mono text-xs"
                    title={callIdTitle(call)}
                  >
                    {call.sip_call_id || call.linked_id || "-"}
                    {call.sip_call_ids && call.sip_call_ids.length > 1 && (
                      <span className="ml-1 text-muted-foreground/70">
                        +{call.sip_call_ids.length - 1}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="font-mono text-xs">{call.caller_id_num || "-"}</div>
                    {call.caller_id_name && (
                      <div className="text-xs text-muted-foreground">
                        {call.caller_id_name}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="font-mono text-xs">
                      {call.connected_line_num || "-"}
                    </div>
                    {call.connected_line_name && (
                      <div className="text-xs text-muted-foreground">
                        {call.connected_line_name}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        call.direction === "inbound"
                          ? "info"
                          : call.direction === "outbound"
                            ? "secondary"
                            : "neutral"
                      }
                    >
                      {call.direction || "-"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span>{call.dial_status || call.state || "-"}</span>
                    {legs.length > 1 && (
                      <div className="text-xs text-muted-foreground">
                        {channelLabel(legs.length)}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {formatDuration(call.duration_seconds)}
                  </TableCell>
                  <TableCell className="text-xs">
                    {call.hangup_cause_txt || call.hangup_cause || "-"}
                  </TableCell>
                  <TableCell>
                    {canExpand && (
                      <button
                        onClick={() =>
                          setExpandedCallId(isExpanded ? null : call.id)
                        }
                        className="cursor-pointer rounded-sm p-1 transition-colors duration-150 hover:bg-muted"
                        aria-label="Toggle call details"
                        aria-expanded={isExpanded}
                        title="Channels and SIP details"
                      >
                        {isExpanded ? (
                          <ChevronUp size={14} />
                        ) : (
                          <ChevronDown size={14} />
                        )}
                      </button>
                    )}
                  </TableCell>
                </TableRow>
                {isExpanded && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={9} className="bg-muted/30 px-6 py-4">
                      <div className="space-y-4">
                        {legs.length > 0 && (
                          <div>
                            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                              Channels ({call.channel_count || legs.length})
                            </h3>
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Channel</TableHead>
                                  <TableHead>SIP Call-ID</TableHead>
                                  <TableHead>Direction</TableHead>
                                  <TableHead>State</TableHead>
                                  <TableHead>Started</TableHead>
                                  <TableHead>Duration</TableHead>
                                  <TableHead>Hangup Cause</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {legs.map((leg) => (
                                  <TableRow key={leg.id}>
                                    <TableCell
                                      className="max-w-[280px] truncate font-mono text-xs"
                                      title={leg.channel ?? ""}
                                    >
                                      {leg.channel || "-"}
                                    </TableCell>
                                    {/* Only PJSIP legs have a SIP dialog; Local
                                        and WebSocket legs never will, so the dash
                                        here is expected rather than missing data. */}
                                    <TableCell
                                      className="max-w-[220px] truncate font-mono text-xs"
                                      title={
                                        leg.sip_call_id ??
                                        "No SIP dialog on this channel type"
                                      }
                                    >
                                      {leg.sip_call_id || (
                                        <span className="text-muted-foreground/60">—</span>
                                      )}
                                    </TableCell>
                                    <TableCell className="text-xs text-muted-foreground">
                                      {leg.direction || "-"}
                                    </TableCell>
                                    <TableCell>
                                      <Badge
                                        variant={
                                          leg.state === "Up"
                                            ? "success"
                                            : leg.state === "Ringing" ||
                                                leg.state === "Ring"
                                              ? "warning"
                                              : "neutral"
                                        }
                                      >
                                        {leg.dial_status || leg.state || "-"}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="whitespace-nowrap text-xs">
                                      {formatTime(leg.start_time)}
                                    </TableCell>
                                    <TableCell className="font-mono text-xs">
                                      {formatDuration(leg.duration_seconds)}
                                    </TableCell>
                                    <TableCell className="text-xs">
                                      {leg.hangup_cause_txt ||
                                        leg.hangup_cause ||
                                        "-"}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        )}
                        {hasHeaders && (
                          <div>
                            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                              SIP Invite Headers
                            </h3>
                            <pre className="whitespace-pre-wrap break-all font-mono text-xs text-muted-foreground">
                              {JSON.stringify(call.invite_headers, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground tabular-nums">
            Page {page + 1} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              <ChevronLeft size={14} /> Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              Next <ChevronRight size={14} />
            </Button>
          </div>
        </div>
      )}
      </div>
      </Section>
    </div>
  );
}
